import { ArrowRight, CheckCircle2, Circle, Loader2, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Container } from '../../components/ui/Container';
import { PageHero } from '../../components/ui/PageHero';
import { pb } from '../../lib/pb';

const MIN_LENGTH = 6;

const PASSWORD_RULES: { label: string; test: (pw: string) => boolean }[] = [
  { label: `At least ${MIN_LENGTH} characters`, test: (pw) => pw.length >= MIN_LENGTH },
  { label: 'One capital letter', test: (pw) => /[A-Z]/.test(pw) },
  { label: 'One number', test: (pw) => /[0-9]/.test(pw) },
  { label: 'One special character', test: (pw) => /[^A-Za-z0-9]/.test(pw) },
];

/**
 * Branded landing page for PocketBase's password-reset confirmation link.
 * PB sends an email containing this URL with the reset token in the path; we
 * collect a new password, POST the confirm endpoint, and route the user back
 * to /login.
 */
export function ConfirmPasswordReset(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [state, setState] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorText, setErrorText] = useState<string>('');

  useEffect(() => {
    const previous = document.title;
    document.title = 'Reset password | Sunset Harbor Condominiums';
    return () => {
      document.title = previous;
    };
  }, []);

  const ruleResults = PASSWORD_RULES.map((rule) => ({ label: rule.label, met: rule.test(next) }));
  const allRulesMet = ruleResults.every((r) => r.met);
  const passwordsMatch = next.length > 0 && next === confirm;
  const canSubmit = !!token && allRulesMet && passwordsMatch && !submitting;

  async function onSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!canSubmit || !token) return;
    setSubmitting(true);
    setState('idle');
    setErrorText('');
    try {
      await pb.collection('users').confirmPasswordReset(token, next, confirm);
      setState('success');
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    } catch (err) {
      console.error(err);
      setState('error');
      setErrorText(
        'This link has expired. Ask the board to send you a new password reset email, then open the link within 24 hours.',
      );
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <>
        <PageHero
          eyebrow="Account"
          title="Reset password"
          lead="The link we used to get here is missing a token."
        />
        <section className="section bg-brand-white">
          <Container>
            <div className="mx-auto max-w-md rounded-2xl border border-brand-surface bg-brand-white p-8 shadow-card md:p-10">
              <p className="text-sm text-brand-body-dark">
                Please open the link from your email again, or contact the board.
              </p>
              <Link
                to="/contact"
                className="mt-6 inline-flex h-12 w-full items-center justify-center rounded-md border border-brand-surface bg-brand-white px-5 text-sm font-semibold text-brand-charcoal hover:bg-brand-surface"
              >
                Contact the board
              </Link>
            </div>
          </Container>
        </section>
      </>
    );
  }

  return (
    <>
      <PageHero
        eyebrow="Account"
        title="Reset your password"
        lead="Pick a new password. The rules are listed below the field."
      />

      <section className="section bg-brand-white">
        <Container>
          <div className="mx-auto max-w-md rounded-2xl border border-brand-surface bg-brand-white p-8 shadow-card md:p-10">
            {state === 'success' ? (
              <div>
                <div className="flex items-start gap-3">
                  <span
                    className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-brand-yellow text-brand-charcoal"
                    aria-hidden="true"
                  >
                    <CheckCircle2 size={22} strokeWidth={2} />
                  </span>
                  <div>
                    <h2 className="text-xl font-bold text-brand-charcoal">Password updated</h2>
                    <p className="mt-2 text-sm leading-relaxed text-brand-body-dark">
                      Redirecting you to sign in...
                    </p>
                  </div>
                </div>
              </div>
            ) : null}

            {state === 'error' ? (
              <div
                role="alert"
                className="mb-6 flex items-start gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
              >
                <XCircle size={18} className="mt-0.5 shrink-0" aria-hidden="true" />
                <span>{errorText}</span>
              </div>
            ) : null}

            {state !== 'success' ? (
              <form onSubmit={onSubmit} noValidate>
                <label className="block">
                  <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-brand-body-medium">
                    New password
                  </span>
                  <input
                    type="password"
                    name="next"
                    autoComplete="new-password"
                    required
                    value={next}
                    onChange={(e) => setNext(e.target.value)}
                    disabled={submitting}
                    className="mt-2 block h-11 w-full rounded-md border border-brand-surface bg-brand-white px-3 text-base text-brand-charcoal focus:border-brand-yellow focus:outline-none focus:ring-2 focus:ring-brand-yellow"
                  />
                  <div className="mt-3" aria-live="polite">
                    <ul className="space-y-1.5">
                      {ruleResults.map((rule) => (
                        <li key={rule.label} className="flex items-center gap-2 text-xs">
                          {rule.met ? (
                            <CheckCircle2
                              size={15}
                              className="shrink-0 text-green-600"
                              aria-hidden="true"
                            />
                          ) : (
                            <Circle
                              size={15}
                              className="shrink-0 text-brand-body-medium/50"
                              aria-hidden="true"
                            />
                          )}
                          <span className={rule.met ? 'text-green-700' : 'text-brand-body-medium'}>
                            {rule.label}
                            <span className="sr-only">{rule.met ? ' met' : ' not yet met'}</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </label>

                <label className="mt-5 block">
                  <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-brand-body-medium">
                    Confirm new password
                  </span>
                  <input
                    type="password"
                    name="confirm"
                    autoComplete="new-password"
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    disabled={submitting}
                    className="mt-2 block h-11 w-full rounded-md border border-brand-surface bg-brand-white px-3 text-base text-brand-charcoal focus:border-brand-yellow focus:outline-none focus:ring-2 focus:ring-brand-yellow"
                  />
                  {confirm.length > 0 ? (
                    <span
                      className={`mt-2 flex items-center gap-2 text-xs font-medium ${
                        passwordsMatch ? 'text-green-700' : 'text-red-700'
                      }`}
                      aria-live="polite"
                    >
                      {passwordsMatch ? (
                        <CheckCircle2 size={14} className="shrink-0" aria-hidden="true" />
                      ) : (
                        <Circle size={14} className="shrink-0" aria-hidden="true" />
                      )}
                      {passwordsMatch ? 'Passwords match' : 'Passwords do not match yet'}
                    </span>
                  ) : null}
                </label>

                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="mt-6 inline-flex h-12 w-full items-center justify-center gap-2 rounded-md bg-brand-yellow px-5 text-sm font-semibold text-brand-charcoal transition-colors hover:bg-brand-yellow-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-yellow focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" aria-hidden="true" />
                      Updating...
                    </>
                  ) : (
                    <>
                      Save new password
                      <ArrowRight size={16} aria-hidden="true" />
                    </>
                  )}
                </button>
              </form>
            ) : null}
          </div>
        </Container>
      </section>
    </>
  );
}
